// BizTracker Ishida Model Finder — deterministic, evidence-bounded.
//
// Specification §10: recommendations come from structured rules and verified
// model data, never from free-form guessing. §30 adds the supplier reality —
// a model is eligible only when its lifecycle permits recommendation, its U.S.
// availability is verified and its commercial policy has not expired. All three
// are decided upstream in the export step and arrive as `finder_eligible`; this
// module does not re-derive commercial policy and holds no copy of it.
//
// THE HARD PART IS THE UNKNOWN. Astra II is the only eligible model today and
// its specifications are catalog-sourced, not manual-verified (§27). So this
// finder distinguishes three answers, not two:
//
//   matched     — a verified capability satisfies the requirement
//   rejected    — a verified capability contradicts it. This is a real "no".
//   unverified  — nothing has confirmed it either way
//
// Silence is never a "no", and it is never a "yes". An unverified capability
// cannot reject a model and cannot earn it a point, and the result card says so
// out loud rather than quietly implying the model fits.

// Numeric requirements are "at least this much"; the rest are booleans or
// enumerated values compared for equality.
const NUMERIC_REQUIREMENTS = new Set(['plu_capacity', 'scale_quantity']);

// Requirements that describe the buyer rather than the machine. They shape the
// conversation and the lead, but no scale specification answers them, so they
// are never scored against capabilities.
const CONTEXT_REQUIREMENTS = new Set([
  'business_type', 'counter_style', 'current_pos', 'existing_model',
  'scale_quantity', 'label_requirements', 'notes'
]);

export const FINDER_QUESTIONS = [
  {
    id: 'business_type',
    label: 'What kind of business is this for?',
    type: 'select',
    options: ['Grocery store', 'Deli', 'Meat market', 'Seafood market', 'Bakery', 'Produce', 'Convenience store', 'Other']
  },
  {
    id: 'required_functions',
    label: 'What does the scale need to do?',
    type: 'multiselect',
    options: [
      { value: 'weigh_only', label: 'Weigh only' },
      { value: 'price_computing', label: 'Price computing' },
      { value: 'label_printing', label: 'Print labels' },
      { value: 'receipt_printing', label: 'Print receipts' },
      { value: 'weighted_barcode', label: 'Weighted barcodes' },
      { value: 'networking', label: 'Networking' },
      { value: 'pos_integration', label: 'POS integration' }
    ]
  },
  {
    id: 'counter_style',
    label: 'How is it used at the counter?',
    type: 'select',
    options: ['Operator facing', 'Customer facing', 'Self service', 'Back-room prepack']
  },
  {
    id: 'plu_capacity',
    label: 'Roughly how many PLUs do you need?',
    type: 'select',
    options: ['Under 100', '100 to 500', '500 to 2000', 'More than 2000', 'Not sure']
  },
  {
    id: 'label_requirements',
    label: 'What label do you need to print?',
    type: 'select',
    options: ['Standard price label', 'Safe-handling label', 'Extended text', 'Blank or continuous stock', 'Not sure']
  },
  {
    id: 'scale_quantity',
    label: 'How many scales?',
    type: 'select',
    options: ['1', '2 to 4', '5 to 10', 'More than 10']
  },
  {
    id: 'current_pos',
    label: 'What POS system do you run?',
    type: 'text'
  },
  {
    id: 'existing_model',
    label: 'If you are replacing a scale, which model?',
    type: 'text'
  }
];

// A model's verified capabilities, or null when nothing has been verified.
// `specifications` only exists on a record whose facts a manual has confirmed —
// the export step withholds catalog-sourced claims.
const verifiedSpecs = model =>
  (model && typeof model.specifications === 'object' && model.specifications) || null;

function evaluateRequirement(key, wanted, specs) {
  if (wanted === undefined || wanted === null || wanted === '' || wanted === false) return 'unstated';
  if (CONTEXT_REQUIREMENTS.has(key)) return 'context';
  if (!specs || !Object.hasOwn(specs, key)) return 'unverified';

  const actual = specs[key];
  if (actual === undefined || actual === null) return 'unverified';

  if (NUMERIC_REQUIREMENTS.has(key)) {
    const need = Number(wanted);
    const has = Number(actual);
    if (!Number.isFinite(need) || !Number.isFinite(has)) return 'unverified';
    return has >= need ? 'matched' : 'rejected';
  }

  if (typeof actual === 'boolean') return actual === Boolean(wanted) ? 'matched' : 'rejected';

  return String(actual).toLowerCase() === String(wanted).toLowerCase() ? 'matched' : 'rejected';
}

export function scoreModel(requirements = {}, model = {}) {
  const specs = verifiedSpecs(model);
  const matched = [];
  const rejected = [];
  const unverified = [];

  for (const [key, wanted] of Object.entries(requirements ?? {})) {
    switch (evaluateRequirement(key, wanted, specs)) {
      case 'matched': matched.push(key); break;
      case 'rejected': rejected.push(key); break;
      case 'unverified': unverified.push(key); break;
      default: break;
    }
  }

  return {
    // A documented contradiction is the only thing that removes a model. An
    // unchecked capability leaves it in the running and says so.
    eligible: model.finder_eligible === true && rejected.length === 0,
    points: matched.length,
    matched,
    rejected,
    unverified,
    specifications_verified: specs !== null
  };
}

const MOUNTING_ORDER = { counter: 0, pole: 1, hanging: 2 };
export function mountingRank(model) {
  const r = MOUNTING_ORDER[model && model.mounting];
  return r === undefined ? 3 : r;
}

export function rankIshidaModels(requirements, models) {
  return (models ?? [])
    .filter(model => model && model.finder_eligible === true)
    .map(model => ({ model, score: scoreModel(requirements, model) }))
    .filter(result => result.score.eligible)
    // Equally good matches are ordered counter scales first, then pole, then hanging (the specialty form), and finally by model
    // name, so the same inputs always give the same order whatever order the catalog arrived in. Mounting only breaks ties:
    // it never changes whether a model matches or how many points it scores.
    .sort((a, b) =>
      b.score.points - a.score.points ||
      mountingRank(a.model) - mountingRank(b.model) ||
      String(a.model.model).localeCompare(String(b.model.model)));
}

// B4 — where an Ishida enquiry actually goes.
//
// This pointed at an `ishida-quote` page that nobody created and nobody will: an Ishida
// enquiry converts on the Contact page the site already has. (The path is described
// rather than spelled, because the link checker reads comments too, and a comment about
// a dead page would otherwise report as a dead link.) `topic` records which
// enquiry it is. The contact form does not read these parameters yet; they are carried so
// that a later enhancement can preselect the topic, and so this link needs no second
// edit when it does.
//
// Named once because three call sites render it, and three copies of a URL drift.
// Callers that append a parameter use `&amp;`, not a bare `&`: inside an HTML attribute
// a bare ampersand begins a character reference, and the legacy rules decode some of
// them without the semicolon.
export const ISHIDA_QUOTE_CONTACT = '/pages/contact?topic=ishida-quote';

const CTA_LABEL = {
  add_to_cart: 'Add to Cart',
  request_quote: 'Request a Quote',
  request_review: 'Talk With an Ishida Specialist',
  view_alternatives: 'See Replacement Options'
};

// Model names and requirement keys come from a JSON file, but a result card is
// still HTML. Everything interpolated is escaped: a model name is data.
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const humanise = key =>
  String(key).replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()).replace(/\bpos\b/i, 'POS');

// Money arrives as `{ amount, currency }` in MAJOR units — dollars, not
// cents. Both halves are required: an amount with no currency is not a price,
// and inventing a default here is how a Canadian store quietly starts showing
// USD. Returns null rather than guessing, and the caller renders nothing.
export function formatMoney(money) {
  if (!money || typeof money.amount !== 'number' || !Number.isFinite(money.amount)) return null;
  if (typeof money.currency !== 'string' || money.currency === '') return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: money.currency
    }).format(money.amount);
  } catch {
    return null;
  }
}


// LABEL COMPATIBILITY, shown beside the model that cites it.
//
// This is the manufacturer's and supplier's statement of which labels a family takes, read from the 2026 retail labels
// catalog and passed through the export untouched (the source stays in data/, never on the page). The card says the labels
// are LISTED as compatible and never claims fit in its own voice: physical fit (width) is necessary and not sufficient - liner,
// adhesive, core, sensor and firmware all matter - so the next sentence says we confirm compatibility before an order. A part number the catalog printed as "Consult" is
// shown as exactly that. No label price is ever rendered; the catalog's list prices are reference
// data and not a BizTracker selling price.
function labelLine(label) {
  const id = label.part_number
    ? 'Part ' + escapeHtml(label.part_number)
    : 'Part number: ask us';
  const bits = [];
  if (typeof label.width_mm === 'number') bits.push(escapeHtml(label.width_mm) + ' mm wide');
  if (label.length) bits.push(escapeHtml(label.length));
  else if (typeof label.length_mm === 'number') bits.push(escapeHtml(label.length_mm) + ' mm long');
  if (label.type) bits.push(escapeHtml(label.type));
  if (typeof label.labels_per_roll === 'number') bits.push(escapeHtml(label.labels_per_roll) + ' labels per roll');
  if (typeof label.rolls_per_case === 'number') bits.push(escapeHtml(label.rolls_per_case) + ' rolls per case');
  return '<li>' + id + (bits.length ? ' — ' + bits.join(', ') : '') + '</li>';
}

export function renderLabelCompatibility(model, context) {
  const names = Array.isArray(model?.label_groups) ? model.label_groups : [];
  const groups = context && context.label_groups ? context.label_groups : {};
  const present = names.filter(n => groups[n] && Array.isArray(groups[n].labels) && groups[n].labels.length);
  const askHref = ISHIDA_QUOTE_CONTACT + '&amp;service=labels&amp;model=' + encodeURIComponent(model?.model ?? '');
  // No compatible items to list for this model: say so plainly and offer help, rather than showing nothing.
  if (!present.length) {
    // Only when the model lists compatible-label groups that cannot be shown. A model that lists none keeps its single quote link.
    if (!names.length) return '';
    return '<p class="ishida-result__label-ask"><a href="' + askHref + '">Ask About Compatible Labels</a></p>';
  }

  const out = [];
  out.push('<details class="ishida-result__labels">');
  out.push('<summary>Compatible Labels &amp; Supplies</summary>');
  out.push('<p class="ishida-result__label-source">Labels and supplies listed as compatible with the ' + escapeHtml(displayModelName(model.model)) +
    '. We confirm compatibility against your scale and label stock before you order.</p>');
  for (const name of present) {
    const group = groups[name];
    if (Array.isArray(group.families) && group.families.length) {
      out.push('<p class="ishida-result__label-group">Listed for: ' + group.families.map(escapeHtml).join(', ') + '</p>');
    }
    out.push('<ul class="ishida-result__label-list">' + group.labels.map(labelLine).join('') + '</ul>');
  }
  out.push('<p class="ishida-result__label-note">Label pricing is confirmed with your quote. ' +
    '<a href="' + askHref + '">Ask about labels and supplies</a></p>');
  out.push('</details>');
  return out.join('');
}

// The highest score and every model that shares it. `ranked` is already sorted by points, so the top group is a prefix. Ties are decided by points
// alone (verified matches); the order inside a tie is mounting then name, which is presentation and never evidence that one model is better.
export function groupTopResults(ranked) {
  const list = Array.isArray(ranked) ? ranked : [];
  if (!list.length) return { top: [], rest: [], tied: false, topPoints: 0 };
  const topPoints = list[0].score.points;
  const top = list.filter(r => r.score.points === topPoints);
  return { top, rest: list.slice(top.length), tied: top.length > 1, topPoints };
}

const NUMBER_WORDS = { 2: 'Two', 3: 'Three', 4: 'Four', 5: 'Five', 6: 'Six', 7: 'Seven', 8: 'Eight', 9: 'Nine', 10: 'Ten' };

// The whole results area. A unique winner gets the single Best match badge. A tie, or a search with nothing to match on, never does.
export function renderResultsBlock(ranked, catalog) {
  const g = groupTopResults(ranked);
  const grid = (items, cls) => '<div class="ishida-results__grid' + (cls ? ' ' + cls : '') + '">' + items.map(r => renderResultCard(r, catalog)).join('') + '</div>';
  const next = '<div class="ishida-result__next"><p>Not sure which to choose? An Ishida specialist confirms the model, configuration and labels with you before anything is quoted.</p>' +
    '<a class="ishida-btn ishida-btn--accent" href="' + ISHIDA_QUOTE_CONTACT + '">Talk With an Ishida Specialist</a></div>';
  const others = g.rest.length
    ? '<h2 class="ishida-results__head ishida-results__head--alt">Other models to consider</h2>' + grid(g.rest, 'ishida-results__grid--compact')
    : '';
  if (g.topPoints === 0) {
    return '<h2 class="ishida-results__head">Models we can show</h2>' +
      '<p class="ishida-results__lead">You did not choose a requirement to match, so these models are shown in a fixed order, which is not a ranking.</p>' +
      grid(g.top, 'ishida-results__grid--compact') + others + next;
  }
  if (g.tied) {
    const n = NUMBER_WORDS[g.top.length] ? g.top.length : g.top.length;
    return '<h2 class="ishida-results__head">Recommended options</h2>' +
      '<p class="ishida-results__lead">' + n + ' models match what you asked for equally well. They are shown in a fixed order, which is not a ranking. An Ishida specialist can help you choose between them.</p>' +
      grid(g.top, 'ishida-results__grid--compact') + others + next;
  }
  return '<h2 class="ishida-results__head">Your best match</h2>' + grid([{ ...g.top[0], best: true }]) + others + next;
}

// Customer-facing capitalization: Ishida styles the family UNI-7. Identifiers (data-model, quote links, catalog keys) keep "Uni-7 Bench".
export function displayModelName(n) {
  return String(n == null ? '' : n).replace(/^Uni-/, 'UNI-');
}

export function renderResultCard(result, labelContext) {
  if (!result || !result.model) return '';
  const { model, score } = result;

  const name = escapeHtml(model.model);
  const shown = escapeHtml(displayModelName(model.model));
  const parts = [];

  // Presentation copy for this model (assets/ishida-presentation.json, built from the verified specification sheet). Optional.
  const pres = labelContext && labelContext.presentation && labelContext.presentation[model.model]
    ? labelContext.presentation[model.model] : null;

  parts.push('<article class="ishida-result' + (result.best === true ? ' ishida-result--best' : '') + '" data-model="' + name + '">');
  // An approved product photograph (assets/<file>.webp, URL supplied by the page through labelContext.imageUrls) replaces the typographic plate.
  const photo = labelContext && labelContext.imageUrls && labelContext.imageUrls[model.model] && labelContext.imageUrls[model.model].url
    ? labelContext.imageUrls[model.model] : null;
  if (photo) {
    parts.push('<div class="ishida-model__media"><img src="' + escapeHtml(photo.url) + '" alt="' + escapeHtml(photo.alt) + '" width="' +
      Number(photo.width || 1200) + '" height="' + Number(photo.height || 900) + '" loading="lazy" decoding="async">' +
      (photo.caption ? '<span class="ishida-model__caption">' + escapeHtml(photo.caption) + '</span>' : '') + '</div>');
  } else if (pres) {
    parts.push('<div class="ishida-model__plate" aria-hidden="true"><span class="ishida-model__series">' + escapeHtml(pres.series_label) +
      '</span><span class="ishida-model__name">' + shown + '</span><span class="ishida-model__form">' + escapeHtml(pres.form) + '</span></div>');
  }
  if (result.best === true) parts.push('<span class="ishida-result__badge">Best match</span>');
  parts.push('<h3 class="ishida-result__title">' + shown + '</h3>');
  if (pres) {
    parts.push('<p class="ishida-result__tagline">' + escapeHtml(pres.tagline) + '</p>');
    if (Array.isArray(pres.chips) && pres.chips.length) {
      parts.push('<ul class="ishida-model__chips">' + pres.chips.map(c => '<li>' + escapeHtml(c) + '</li>').join('') + '</ul>');
    }
    if (Array.isArray(pres.highlights) && pres.highlights.length) {
      parts.push('<dl class="ishida-model__specs">' + pres.highlights.map(h =>
        '<div><dt>' + escapeHtml(h.label) + '</dt><dd>' + escapeHtml(h.value) + '</dd></div>').join('') + '</dl>');
    }
  }

  // Why it fits — only ever from verified matches. With nothing verified this
  // list is empty, and the card says nothing about what the scale can do.
  if (score.matched.length) {
    // Only requirements the model was confirmed to meet are listed here. The evidence behind each stays in data/ and is never shown to customers.
    parts.push('<h4 class="ishida-result__reasons-lead">Key Features</h4>');
    parts.push('<ul class="ishida-result__reasons">');
    for (const key of score.matched) {
      parts.push('<li>' + escapeHtml(humanise(key)) + '</li>');
    }
    parts.push('</ul>');
  }

  // What could not be checked. Named explicitly rather than omitted, so an
  // unverified capability reads as an open question instead of a quiet yes.
  if (score.unverified.length) {
    parts.push('<p class="ishida-result__unverified">We have <strong>not yet confirmed</strong> these for this model: ' +
      score.unverified.map(k => escapeHtml(humanise(k))).join(', ') +
      '. We confirm them with you before quoting.</p>');
  }

  if (!score.specifications_verified) {
    parts.push('<p class="ishida-result__note">Full specifications for this model are not shown yet. An Ishida specialist can confirm them for you.</p>');
  }

  const labelBlock = renderLabelCompatibility(model, labelContext);
  if (labelBlock) parts.push(labelBlock);

  // The commercial control, taken from the export rather than decided here.
  const cta = CTA_LABEL[model.cta] ?? CTA_LABEL.request_quote;
  const formatted = model.show_price === true ? formatMoney(model.price) : null;

  // Purchasable means all of it: the export said so, a formatted price exists,
  // and there is a variant to post. Any one missing and this is a quote.
  const purchasable = model.add_to_cart === true && formatted !== null && model.variant_id;

  // THE PRICE AND THE CART ARE INDEPENDENT — which is the entire reason §5.3
  // keeps price_visibility and purchase_mode on separate axes. A model may be
  // permitted to show a price and not yet be purchasable; Astra II is exactly
  // that today, priced but with no Shopify variant to post to. Rendering the
  // price only inside the cart branch made such a model show no price at all.
  //
  // The prefix matters as much as the number: on a family whose configurations
  // are priced differently, an unqualified figure claims to be the price of all
  // of them.
  if (formatted !== null) {
    // The price is TEXT CONTENT. It was previously an empty span with the
    // amount hidden in a data attribute, so the card rendered a blank price
    // and the test that "proved" it passed on the attribute alone.
    parts.push('<p class="ishida-result__price">' +
      (model.price_prefix ? escapeHtml(capitalizeFirst(model.price_prefix)) + ' ' : '') +
      '<span class="ishida-price">' + escapeHtml(formatted) + '</span></p>');
  } else if (model.price_label) {
    parts.push('<p class="ishida-result__price-label">' + escapeHtml(model.price_label) + '</p>');
  }

  if (purchasable) {
    // A real Shopify cart POST rather than a button with no handler.
    parts.push(
      '<form method="post" action="/cart/add" class="ishida-result__form">' +
      '<input type="hidden" name="id" value="' + escapeHtml(model.variant_id) + '">' +
      '<input type="hidden" name="quantity" value="1">' +
      '<button type="submit" class="ishida-result__cta">' + escapeHtml(cta) + '</button>' +
      '</form>'
    );

    if (model.url) {
      parts.push('<a class="ishida-result__details" href="' + escapeHtml(model.url) + '">View full specifications</a>');
    }
  } else {
    parts.push('<a class="ishida-result__cta" data-cta="' + escapeHtml(model.cta ?? 'request_quote') +
      '" href="' + ISHIDA_QUOTE_CONTACT + '&amp;model=' + encodeURIComponent(model.model ?? '') +
      '">' + escapeHtml(cta) + '</a>');
  }

  parts.push('</article>');
  return parts.join('');
}

// ---------------------------------------------------------------- DOM wiring

// The landing page links each counter to ?business_type=...; select it if it is one of the options the form offers.
export function prefillBusinessType(form, search) {
  try {
    const wanted = new URLSearchParams(search || '').get('business_type');
    const select = form && form.querySelector && form.querySelector('select[name="business_type"]');
    if (!wanted || !select) return false;
    const match = Array.from(select.options || []).find(o => o.value === wanted);
    if (!match) return false;
    select.value = wanted;
    return true;
  } catch (e) {
    return false;
  }
}

// The catalog stores the prefix as written for sentence use ("from"); the card shows it as the hub does ("From $1,919.00").
export function capitalizeFirst(s) {
  s = String(s || '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ----------------------------------------------------------- mobile steps
// On a narrow screen the eight questions are shown three at a time. This only changes which questions are VISIBLE:
// every field stays in the one form (hidden is not disabled), so FormData, and therefore the matching, is unchanged.

export const STEPS_PER_PAGE = 3;

// How many pages `total` steps make at `perPage` a page.
export function stepPageCount(total, perPage = STEPS_PER_PAGE) {
  return Math.max(1, Math.ceil(total / perPage));
}

// Shows the steps on `page` (0-based, clamped) and hides the rest. `steps` is any list of objects with a `hidden` property.
export function showStepPage(steps, page, perPage = STEPS_PER_PAGE) {
  const pages = stepPageCount(steps.length, perPage);
  const current = Math.min(Math.max(0, page), pages - 1);
  steps.forEach((step, i) => { step.hidden = Math.floor(i / perPage) !== current; });
  return { page: current, pages };
}

// Clears any paging: every step visible.
export function showAllSteps(steps) {
  steps.forEach(step => { step.hidden = false; });
}

export function mountStepper(form, win) {
  if (!form || !win || typeof win.matchMedia !== 'function') return;
  const steps = Array.from(form.querySelectorAll('.ishida-step'));
  const actions = form.querySelector('.ishida-finder__actions');
  const submit = form.querySelector('.ishida-finder__submit');
  if (steps.length <= STEPS_PER_PAGE || !actions || !submit) return;

  // Number the questions explicitly: a CSS counter skips hidden steps, so every page would otherwise start again at 1.
  steps.forEach((s, i) => { s.dataset.n = String(i + 1); });

  const doc = form.ownerDocument;
  const nav = doc.createElement('div');
  nav.className = 'ishida-stepper';
  nav.innerHTML =
    '<p class="ishida-stepper__status" role="status" aria-live="polite"></p>' +
    '<div class="ishida-stepper__bar" aria-hidden="true"><span class="ishida-stepper__fill"></span></div>' +
    '<div class="ishida-stepper__buttons">' +
    '<button type="button" class="ishida-btn ishida-btn--outline-dark" data-step-back>Back</button>' +
    '<button type="button" class="ishida-btn ishida-btn--primary" data-step-next>Next</button></div>';
  form.insertBefore(nav, actions);
  const status = nav.querySelector('.ishida-stepper__status');
  const fill = nav.querySelector('.ishida-stepper__fill');
  const back = nav.querySelector('[data-step-back]');
  const next = nav.querySelector('[data-step-next]');
  const mq = win.matchMedia('(max-width: 749px)');
  let page = 0;

  function render(focus) {
    if (!mq.matches) {
      showAllSteps(steps);
      nav.hidden = true;
      submit.hidden = false;
      return;
    }
    const r = showStepPage(steps, page);
    page = r.page;
    nav.hidden = false;
    status.textContent = 'Step ' + (page + 1) + ' of ' + r.pages;
    fill.style.width = Math.round(((page + 1) / r.pages) * 100) + '%';
    back.hidden = page === 0;
    next.hidden = page === r.pages - 1;
    submit.hidden = page !== r.pages - 1;
    if (focus) {
      const first = steps.find(s => !s.hidden);
      const target = first && first.querySelector('select, input, legend, label');
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
      if (first && typeof first.scrollIntoView === 'function') first.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  back.addEventListener('click', () => { page -= 1; render(true); });
  next.addEventListener('click', () => { page += 1; render(true); });
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', () => render(false));
  render(false);
}

export function mountFinder(root, catalog) {
  if (!root || !catalog) return;
  const form = root.querySelector('[data-ishida-finder-form]');
  const results = root.querySelector('[data-ishida-finder-results]');
  if (!form || !results) return;
  if (typeof window !== 'undefined') prefillBusinessType(form, window.location && window.location.search);
  if (typeof window !== 'undefined') mountStepper(form, window);

  form.addEventListener('submit', event => {
    event.preventDefault();
    const data = new FormData(form);
    const requirements = {};

    for (const value of data.getAll('required_functions')) requirements[value] = true;
    for (const q of FINDER_QUESTIONS) {
      if (q.id === 'required_functions') continue;
      const v = data.get(q.id);
      if (v) requirements[q.id] = v;
    }

    const ranked = rankIshidaModels(requirements, catalog.models);
    if (!ranked.length) {
      results.innerHTML =
        '<p class="ishida-result__none">We do not have a match for that combination yet. ' +
        'Tell us what you need and an Ishida specialist will confirm the right model for you.</p>' +
        '<a class="ishida-result__cta" href="' + ISHIDA_QUOTE_CONTACT +
        '">Talk With an Ishida Specialist</a>';
      return;
    }

    results.innerHTML = renderResultsBlock(ranked, catalog);
    if (typeof results.scrollIntoView === 'function') {
      const calm = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      results.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
    }
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    for (const root of document.querySelectorAll('[data-ishida-finder]')) {
      const src = root.getAttribute('data-catalog-url');
      if (!src) continue;
      const presentationSrc = root.getAttribute('data-presentation-url');
      fetch(src)
        .then(r => r.json())
        .then(catalog => {
          // The presentation copy is optional: a failed fetch leaves the cards exactly as they were.
          if (!presentationSrc) return catalog;
          return fetch(presentationSrc).then(r => r.json()).then(p => ({ ...catalog, presentation: p }), () => catalog);
        })
        .then(catalog => {
          // Approved photograph URLs, written by the page itself (theme asset URLs need Liquid). Optional and failure-tolerant.
          try {
            const el = root.querySelector('[data-ishida-image-urls]');
            const imageUrls = el ? JSON.parse(el.textContent || '{}') : null;
            return imageUrls ? { ...catalog, imageUrls } : catalog;
          } catch (e) {
            return catalog;
          }
        })
        .then(catalog => mountFinder(root, catalog))
        .catch(() => {
          const results = root.querySelector('[data-ishida-finder-results]');
          if (results) {
            results.innerHTML =
              '<p class="ishida-result__none">The model finder is unavailable right now. ' +
              '<a href="' + ISHIDA_QUOTE_CONTACT + '">Talk with an Ishida specialist</a>.</p>';
          }
        });
    }
  });
}
